import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Globe } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useT, LANGUAGES } from "@/lib/i18n";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useT();

  const links = [
    { to: "/", label: t("nav.home"), testid: NAV.link_home },
    { to: "/equipos-dermo-faciales", label: t("nav.machines"), testid: NAV.link_machines },
    { to: "/tecnologia-dermo-facial", label: t("nav.technology"), testid: NAV.link_technology },
    { to: "/sectores-aparatologia-estetica", label: t("nav.sectors"), testid: NAV.link_sectors },
    { to: "/casos-de-exito-dermo-facial", label: t("nav.cases"), testid: NAV.link_cases },
    { to: "/blog-dermo-facial", label: t("nav.blog"), testid: NAV.link_blog },
    { to: "/asesor-dermo-facial-ia", label: t("nav.assistant"), testid: NAV.link_assistant },
    { to: "/contacto", label: t("nav.contact"), testid: NAV.link_contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setLangOpen(false); }, [location.pathname]);

  const currentLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/95 backdrop-blur-xl" : "bg-black"
      }`}
      style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
    >
      <div className="w-full px-6 md:px-10 h-20 flex items-center justify-between gap-6">
        {/* Logo (full, never cropped, 60px height) */}
        <Link to="/" data-testid={NAV.brand} className="flex items-center shrink-0">
          <img
            src="/asirox-logo.png"
            alt="Asirox"
            className="logo-white h-[60px] w-auto"
          />
        </Link>

        {/* Center nav — desktop only at >= 1280px so logo + links never collide */}
        <nav className="hidden xl:flex items-center gap-6 2xl:gap-8 flex-1 justify-center">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-body font-medium link-underline whitespace-nowrap ${
                  isActive ? "text-[#ff9c00]" : "text-white/85 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster: language + CTA */}
        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          {/* Language switcher */}
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              data-testid="nav-language-toggle"
              className="flex items-center gap-1.5 px-3 py-2 text-white/85 hover:text-white text-sm font-mono uppercase border border-white/15 hover:border-[#ff9c00] transition"
              aria-label="Cambiar idioma"
            >
              <Globe size={14} strokeWidth={1.6}/>
              <span>{currentLang.label}</span>
            </button>
            {langOpen && (
              <div data-testid="nav-language-menu" className="absolute right-0 top-full mt-2 bg-black border border-white/15 min-w-[150px] z-50">
                {LANGUAGES.map((L) => (
                  <button
                    key={L.code}
                    data-testid={`nav-language-${L.code}`}
                    onClick={() => { setLang(L.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/5 transition flex items-center justify-between ${
                      L.code === lang ? "text-[#ff9c00]" : "text-white/85"
                    }`}
                  >
                    <span>{L.name}</span>
                    <span className="font-mono text-xs">{L.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/contacto"
            data-testid={NAV.cta_demo}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-[#ff9c00] text-black font-bold text-xs uppercase tracking-wider hover:bg-[#ffb133] transition-colors whitespace-nowrap"
            style={{ boxShadow: "0 0 20px rgba(255,156,0,0.3)" }}
          >
            {t("nav.cta_demo")} <ArrowUpRight size={14} strokeWidth={2.2} />
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.mobile_toggle}
            className="xl:hidden p-2 text-white"
            aria-label="Menú"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-black border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-base font-body ${isActive ? "text-[#ff9c00]" : "text-white/85"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contacto" className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#ff9c00] text-black font-bold text-sm uppercase">
              {t("nav.cta_demo")} <ArrowUpRight size={16}/>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
